import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import supabase from "../services/supabaseClient";
import { getAuthRedirectUrl } from "../lib/authRoutes";
import { summarizeRoom } from "../services/roomSummary";
import {
  normalizeWorldForSupabase,
  applyIslandUnlockRules,
  buildStarterHavenWorld,
  isStarterHavenOnlyWorld,
  stripHavenFromWorldPayload,
  splitWorldPayload,
  surfaceItemsByRoom,
  withWhiteboardImage,
  withBulletinImage,
  buildWorldPayload,
  locationFromRoomId,
  forEachRoom,
  resolveSurfaceItemFromHotspot,
  surfaceItemsWithDeskEdits,
  deskDocumentsForRoom,
} from "../lib/worldJson";
import { fetchWorldForUser, fetchUserProfileForUser } from "../lib/fetchWorld";
import { buildSkippedUserProfile, upsertSkippedUserProfile } from "../lib/userProfileDefaults";
import { deleteUserWorldData } from "../lib/resetUserWorld";
import { clearDeskPopupSeen } from "../lib/hotspotPopupStorage";
import {
  requestUnityWorldSync,
  setUnityBuildingColorsAssignedHandler,
  setUnityIslandRemovedHandler,
  setUnityLayoutCorrectedHandler,
} from "../lib/unityEvents";
import { applyLayoutCorrection, compactWorldLayout, needsCompaction } from "../lib/worldLayout";
import { carryBuildingModels, needsBuildingModels, withAssignedColors } from "../lib/buildingModels";
import { getUnityBridgeCommands } from "../lib/unityCommandBridge";
import { planDeskWrite, mergeIntoDeskDoc } from "../lib/deskWrites";
import {
  isPlannerItem,
  isCalendarEvent,
  isBulletinNote,
  isDayBlock,
  notesForRoom,
  createCalendarEvent,
  updateCalendarEvent as applyCalendarEventPatch,
  createBulletinNote,
  updateBulletinNote as applyBulletinNotePatch,
  createDayBlock,
  updateDayBlock as applyDayBlockPatch,
  createDayEnd,
  isDayEnd,
  toDateKey,
} from "../lib/plannerItems";
import { planLumenCalendarUpdate } from "../lib/lumenCalendar";
import { acceptPlan, buildPlan, dismissPlan, pendingPlanId, setDayEndFromLumen } from "../lib/dayPlanProposal";

// ═══════════════════════════════════════════════════════════════
// SPATIAL CONTEXT — NeuroQuest World State
// Holds spatial hierarchy AND shared surface state
// Persisted to Supabase — world survives refresh and comes back
// ═══════════════════════════════════════════════════════════════

const EMPTY_WORLD = { id: "world-1", name: "My World", districts: [] };

/**
 * Defensive merge: when Claude returns an island with an empty buildings array,
 * restore the existing buildings from the live world rather than silently losing data.
 * Matches islands by id first, falls back to name (case-insensitive) in case Claude
 * regenerated the id. Only restores when the new array is empty AND the existing
 * island had content — no-op for genuinely new islands or islands that still have buildings.
 */
function defensiveMergeBuildings(newWorld, existingWorld) {
  if (!existingWorld?.districts?.length || !newWorld?.districts) return newWorld;

  const existingById = new Map();
  const existingByName = new Map();
  const districtsById = new Map();
  const districtsByName = new Map();
  for (const district of existingWorld.districts) {
    if (district.id) districtsById.set(district.id, district);
    if (district.name) districtsByName.set(district.name.toLowerCase(), district);
    for (const island of district.islands || []) {
      if (island.id) existingById.set(island.id, island);
      if (island.name) existingByName.set(island.name.toLowerCase(), island);
    }
  }

  return {
    ...newWorld,
    districts: newWorld.districts.map(district => {
      const existingDistrict = districtsById.get(district.id)
        ?? districtsByName.get((district.name || "").toLowerCase());
      return {
        ...district,
        ...(existingDistrict?.color ? { color: existingDistrict.color } : {}),
        ...(existingDistrict?.layout ? { layout: existingDistrict.layout } : {}),
        islands: (district.islands || []).map(island => {
          const existing = existingById.get(island.id)
            ?? existingByName.get((island.name || "").toLowerCase());
          if (!existing) return island;

          let merged = island;
          if ((island.buildings || []).length === 0 && existing.buildings?.length > 0) {
            console.warn(`[defensiveMerge] Restoring ${existing.buildings.length} buildings for island "${island.name}". Claude returned empty array`);
            merged = { ...merged, buildings: existing.buildings };
          }
          if (typeof existing.is_unlocked === "boolean") {
            merged = { ...merged, is_unlocked: existing.is_unlocked };
          }
          if (existing.island_id) merged = { ...merged, island_id: existing.island_id };
          if (existing.layout) merged = { ...merged, layout: existing.layout };
          return merged;
        }),
      };
    }),
  };
}

const SpatialContext = createContext(null);

export function SpatialProvider({ children }) {

  // ── Auth state ──
  const [user, setUser]           = useState(null);
  const [appUser, setAppUser]     = useState(null); // row from public.users
  const [authReady, setAuthReady] = useState(false);
  const [sessionBootstrapping, setSessionBootstrapping] = useState(() =>
    /[?#].*(code=|access_token=|error=|type=magiclink|type=signup)/i.test(window.location.href)
  );
  const [authCallbackError, setAuthCallbackError] = useState(null);

  // ── User profile (quiz results) ──
  const [userProfile,        setUserProfile]        = useState(null);
  const [profileLoading,     setProfileLoading]     = useState(false);
  const [profileChecked,     setProfileChecked]     = useState(false);
  const [previousSession,    setPreviousSession]    = useState(null);
  const [currentRoomActivity, setCurrentRoomActivity] = useState(null);
  const [roomSummaryVersion, setRoomSummaryVersion] = useState(0);

  // ── World hierarchy ──
  const [world, setWorld]     = useState(EMPTY_WORLD);
  const [worldReady, setWorldReady] = useState(false);
  const [worldLoadFailed, setWorldLoadFailed] = useState(false);
  const [needsBrainDump, setNeedsBrainDump] = useState(false);
  const [justBuiltWorld, setJustBuiltWorld] = useState(false);
  const [worldSaving, setWorldSaving] = useState(false);
  const [worldSaveError, setWorldSaveError] = useState(null);
  const [worldLastSavedAt, setWorldLastSavedAt] = useState(null);
  const [worldAutosavePending, setWorldAutosavePending] = useState(false);
  const [persistedSurfaceItems, setPersistedSurfaceItems] = useState([]);
  const [roomSurfaceItems, setRoomSurfaceItems] = useState({});

  const [currentLocation, setCurrentLocation] = useState({
    districtId: null, islandId: null, buildingId: null, floorId: null, roomId: null,
  });

  const [mapOpen, setMapOpen]       = useState(false);
  const [mapFocusId, setMapFocusId] = useState(null);

  // ── Shared surface state ──
  const [surfaceState, setSurfaceState] = useState({
    whiteboardCards: [],
    deskDocuments:   [],
    bookshelfDocs:   [],
  });

  const fetchAppUser = async (authUser) => {
    if (!authUser) return null;
    const { data, error } = await supabase
      .from("users")
      .select("id, email, name, company")
      .eq("id", authUser.id)
      .maybeSingle();
    if (error && error.code !== "PGRST116") {
      console.error("Error loading users row:", error);
      return null;
    }
    return data || null;
  };

  const ensureAppUserRow = async (authUser) => {
    const email = authUser.email || "";
    const meta = authUser.user_metadata || {};
    const name =
      meta.name ||
      meta.full_name ||
      (email.includes("@") ? email.split("@")[0] : "User");
    const { error } = await supabase.from("users").upsert(
      {
        id: authUser.id,
        email,
        name,
        company: meta.company || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    );
    if (error) console.error("ensureAppUserRow:", error);
    return fetchAppUser(authUser);
  };

  const loadAppUser = async (authUser) => {
    if (!authUser) {
      setAppUser(null);
      return null;
    }
    let row = await fetchAppUser(authUser);
    if (!row) row = await ensureAppUserRow(authUser);
    setAppUser(row);
    return row;
  };

  const signingOutRef = useRef(false);
  const worldDirtyRef = useRef(false);
  const scheduleFlushRef = useRef(null);
  const flushDebounceTimerRef = useRef(null);
  const flushInFlightRef = useRef(null);
  // Incremented on every delete so in-flight autosaves know not to overwrite
  // locally-deleted items when they sync back from the Supabase response.
  const deleteVersionRef = useRef(0);
  const plannerChangeVersionRef = useRef(0);

  const cancelScheduledFlush = () => {
    if (flushDebounceTimerRef.current != null) {
      clearTimeout(flushDebounceTimerRef.current);
      flushDebounceTimerRef.current = null;
    }
  };
  const worldRef = useRef(world);
  const persistedSurfaceItemsRef = useRef(persistedSurfaceItems);
  const surfaceStateRef = useRef(surfaceState);
  const currentLocationRef = useRef(currentLocation);
  const worldLoadUserIdRef = useRef(null);

  worldRef.current = world;
  persistedSurfaceItemsRef.current = persistedSurfaceItems;
  surfaceStateRef.current = surfaceState;
  currentLocationRef.current = currentLocation;

  const clearAuthState = useCallback(() => {
    setUser(null);
    setAppUser(null);
    setUserProfile(null);
    setProfileChecked(false);
    setWorld(EMPTY_WORLD);
    setPersistedSurfaceItems([]);
    setRoomSurfaceItems({});
    setWorldSaveError(null);
    setWorldReady(false);
    setNeedsBrainDump(false);
    worldLoadUserIdRef.current = null;
  }, []);

  const stripAuthParamsFromUrl = () => {
    if (!window.location.search && !window.location.hash) return;
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // ── Auth: listen for login/logout + magic-link callback ──
  useEffect(() => {
    const bootstrapSession = async () => {
      const params = new URLSearchParams(
        window.location.hash?.replace(/^#/, "") || window.location.search
      );
      const urlError = params.get("error_description") || params.get("error");
      if (urlError) {
        setAuthCallbackError(decodeURIComponent(urlError.replace(/\+/g, " ")));
      }

      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) console.error("getSession:", sessionError);

      const code = new URLSearchParams(window.location.search).get("code");
      if (!session?.user && code) {
        const { data: exchanged, error: exchangeError } =
          await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          console.error("exchangeCodeForSession:", exchangeError);
          setAuthCallbackError(
            exchangeError.message ||
              "Could not complete sign-in from this link. Request a new magic link and open it once."
          );
        } else {
          session = exchanged.session;
        }
      }

      if (session?.user) {
        stripAuthParamsFromUrl();
        setAuthCallbackError(null);
      }

      const authUser = session?.user ?? null;
      setUser(authUser);
      await loadAppUser(authUser);
      setAuthReady(true);
      setSessionBootstrapping(false);
    };

    bootstrapSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Never await Supabase data calls here — it can deadlock auth.signOut()
      if (signingOutRef.current) return;
      if (event === "SIGNED_OUT" || !session?.user) {
        clearAuthState();
        setSessionBootstrapping(false);
        return;
      }

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") {
        setSessionBootstrapping(false);
      }

      const authUser = session.user;
      setUser(authUser);
      setTimeout(() => {
        loadAppUser(authUser);
      }, 0);
    });
    return () => subscription.unsubscribe();
  }, [clearAuthState]);

  
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
      window.sendMagicLink = async (email) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: getAuthRedirectUrl() },
      });
      if (error) console.error("Magic link error:", error.message);
      else console.info("Magic link sent to", email);
      return { error };
    };
    return () => {
      delete window.sendMagicLink;
    };
  }, []);

  // ── Load world from Supabase when user logs in ──
  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setWorldReady(false);
      worldLoadUserIdRef.current = null;
      return undefined;
    }

    // TOKEN_REFRESHED updates user object often — only load once per user id.
    if (worldLoadUserIdRef.current === userId) return undefined;

    let cancelled = false;
    setWorldReady(false);

    const applyLoadedWorld = (normalized, modelsAdded = false) => {
      const { hierarchy, surface_items } = splitWorldPayload(normalized);
      const layoutChanged = needsCompaction(hierarchy.districts);
      setWorld(layoutChanged ? { ...hierarchy, districts: compactWorldLayout(hierarchy.districts) } : hierarchy);
      if (layoutChanged || modelsAdded) {
        worldDirtyRef.current = true;
        scheduleFlushRef.current?.();
      }
      setPersistedSurfaceItems(surface_items);
      setRoomSurfaceItems(surfaceItemsByRoom(surface_items));
      setNeedsBrainDump(isStarterHavenOnlyWorld(normalized.districts));
      setWorldLoadFailed(false);
      setWorldReady(true);
      worldLoadUserIdRef.current = userId;
    };

    const applyUnreadableWorld = (reason, cause) => {
      console.error(`SpatialContext: ${reason} — continuing with an empty world`, cause);
      setWorld(EMPTY_WORLD);
      setPersistedSurfaceItems([]);
      setRoomSurfaceItems({});
      setNeedsBrainDump(false);
      setWorldLoadFailed(true);
      setWorldReady(true);
    };

    const loadWorld = async () => {
      let worldJson = null;
      let error = null;
      try {
        ({ worldJson, error } = await fetchWorldForUser(userId));
      } catch (fetchError) {
        error = fetchError;
      }
      if (cancelled) return;

      if (error) {
        applyUnreadableWorld("world fetch failed", error);
        return;
      }
      if (!worldJson || !worldJson.districts?.length) {
        applyLoadedWorld(buildStarterHavenWorld(userId));
        return;
      }
      try {
        const normalized = normalizeWorldForSupabase(userId, worldJson);
        applyLoadedWorld(normalized, needsBuildingModels(worldJson.districts));
      } catch (normalizeError) {
        applyUnreadableWorld("stored world could not be read", normalizeError);
      }
    };
    loadWorld();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // ── Load user profile from Supabase when user logs in ──
  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const { profile, error } = await fetchUserProfileForUser(user.id);
        if (error) {
          setUserProfile(buildSkippedUserProfile());
        } else if (profile) {
          setUserProfile(profile);
          setPreviousSession({
            last_room_id:  profile.last_room_id  || null,
            last_activity: profile.last_activity || null,
          });
        } else {
          const skipped = buildSkippedUserProfile();
          void upsertSkippedUserProfile(user.id, skipped);
          setUserProfile(skipped);
        }
      } finally {
        setProfileLoading(false);
        setProfileChecked(true);
      }
    };
    loadProfile();
  }, [user]);

  const persistWorldJson = async (payload) => {
    // Fix 2: capture delete version before the async Supabase write.
    // If a delete happens while we're awaiting, the version will have changed and
    // we skip the re-sync so a stale server response can't resurrect a deleted doc.
    const capturedDeleteVersion = deleteVersionRef.current;
    const capturedPlannerVersion = plannerChangeVersionRef.current;

    // Use the already-resolved user.id when available — avoids an unnecessary
    // async getSession() round-trip on every save (can cost 0-1000ms).
    let userId = user?.id;
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;
    }
    if (!userId) {
      const msg = "Not signed in — world was not saved to Supabase.";
      console.error("persistWorldJson:", msg);
      setWorldSaveError(msg);
      setWorldAutosavePending(false);
      return { ok: false, error: msg };
    }

    const normalized = normalizeWorldForSupabase(userId, payload);
    setWorldSaving(true);
    setWorldSaveError(null);

    try {
      // Update first; if no row exists, insert (works without unique index + avoids extra SELECT under RLS)
      const { data: updated, error: updateError } = await supabase
        .from("worlds")
        .update({ world_json: normalized })
        .eq("user_id", userId)
        .select("id");

      if (updateError) {
        console.error("worlds update failed:", updateError);
        setWorldSaveError(updateError.message);
        setWorldAutosavePending(false);
        return { ok: false, error: updateError.message };
      }

      if (!updated?.length) {
        const { data: inserted, error: insertError } = await supabase
          .from("worlds")
          .insert({ user_id: userId, world_json: normalized })
          .select("id");

        if (insertError) {
          console.error("worlds insert failed:", insertError);
          const rlsHint = /policy|permission|42501|row-level security/i.test(insertError.message || "")
            ? `${insertError.message} — Run apps/web-portal/supabase/migrations/20250616000000_worlds_rls.sql in Supabase SQL Editor.`
            : insertError.message;
          setWorldSaveError(rlsHint);
          setWorldAutosavePending(false);
          return { ok: false, error: rlsHint };
        }
        console.info("worlds saved (insert)", inserted?.[0]?.id, "surface_items:", normalized.surface_items?.length ?? 0);
      } else {
        console.info("worlds saved (update)", updated[0].id, "surface_items:", normalized.surface_items?.length ?? 0);
      }

      // Fix 2: only sync server response back to local state if no delete happened
      // while we were awaiting — otherwise the stale server data would resurrect
      // a document the user just deleted.
      const changedDuringSave = plannerChangeVersionRef.current !== capturedPlannerVersion;
      if (deleteVersionRef.current === capturedDeleteVersion && !changedDuringSave) {
        setPersistedSurfaceItems(normalized.surface_items || []);
        setRoomSurfaceItems(prev => {
          const next = surfaceItemsByRoom(normalized.surface_items);
          return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
        });
      }
      if (!changedDuringSave) worldDirtyRef.current = false;
      setWorldLastSavedAt(Date.now());
      setWorldAutosavePending(false);
      return { ok: true, data: normalized, changedDuringSave };
    } finally {
      setWorldSaving(false);
    }
  };

  /** Unity SyncWorldState — work islands only; Haven lives in the baked Unity scene. */
  const getUnityWorldPayload = useCallback(() => {
    const full = buildWorldPayload(world, persistedSurfaceItems);
    return stripHavenFromWorldPayload(full);
  }, [world, persistedSurfaceItems]);

  const flushWorldJson = useCallback(async () => {
    cancelScheduledFlush();
    if (flushInFlightRef.current) {
      // Fix 3: don't just return the stale in-flight promise — chain a fresh flush
      // after it completes so any state change (e.g. a delete) that happened while
      // the prior save was in-flight gets its own write.
      return flushInFlightRef.current.then(() => {
        if (worldDirtyRef.current) return flushWorldJson();
      });
    }

    // Nothing changed since the last successful save — skip the network round-trip.
    // This prevents the close button from doing a redundant Supabase write when the
    // world is already up-to-date (e.g. right after a delete flush completes).
    if (!worldDirtyRef.current) return { ok: true, skipped: true };

    const run = (async () => {
      const hierarchy = worldRef.current;
      if (!hierarchy?.districts?.length) {
        return { ok: true, skipped: true };
      }
      const mergedItems = surfaceItemsWithDeskEdits(
        persistedSurfaceItemsRef.current,
        surfaceStateRef.current?.deskDocuments || []
      );
      const payload = buildWorldPayload(hierarchy, mergedItems);
      const result = await persistWorldJson(payload);
      if (result?.ok && !result.changedDuringSave) worldDirtyRef.current = false;
      return result;
    })();

    flushInFlightRef.current = run;
    try {
      return await run;
    } finally {
      if (flushInFlightRef.current === run) flushInFlightRef.current = null;
    }
  }, []);

  const scheduleFlushWorldJson = useCallback(() => {
    if (!worldDirtyRef.current) return;
    cancelScheduledFlush();
    flushDebounceTimerRef.current = setTimeout(() => {
      flushDebounceTimerRef.current = null;
      void flushWorldJson();
    }, 1500);
  }, [flushWorldJson]);
  scheduleFlushRef.current = scheduleFlushWorldJson;

  useEffect(() => {
    setUnityLayoutCorrectedHandler((correction) => {
      setWorld((prev) => {
        const districts = applyLayoutCorrection(prev.districts, correction);
        return districts === prev.districts ? prev : { ...prev, districts };
      });
      worldDirtyRef.current = true;
      scheduleFlushWorldJson();
    });
    setUnityIslandRemovedHandler((islandId) => {
      setWorld((prev) => ({
        ...prev,
        districts: (prev.districts || []).map((d) =>
          (d.islands || []).some((i) => i.id === islandId)
            ? { ...d, islands: d.islands.filter((i) => i.id !== islandId) }
            : d
        ),
      }));
      worldDirtyRef.current = true;
      scheduleFlushWorldJson();
    });
    setUnityBuildingColorsAssignedHandler((assignments) => {
      setWorld((prev) => {
        const districts = withAssignedColors(prev.districts, assignments);
        return districts === prev.districts ? prev : { ...prev, districts };
      });
      worldDirtyRef.current = true;
      scheduleFlushWorldJson();
    });
    return () => {
      setUnityLayoutCorrectedHandler(null);
      setUnityIslandRemovedHandler(null);
      setUnityBuildingColorsAssignedHandler(null);
    };
  }, [scheduleFlushWorldJson]);

  /** Testing only — wipe persisted world data and restore in-memory new-user state. */
  const resetToNewUser = useCallback(async () => {
    const userId = user?.id;
    if (!userId) {
      return { ok: false, error: "Not signed in" };
    }

    cancelScheduledFlush();
    worldDirtyRef.current = false;

    if (flushInFlightRef.current) {
      try {
        await flushInFlightRef.current;
      } catch (e) {
        console.warn("resetToNewUser: flush before delete failed", e);
      }
    }

    const dbResult = await deleteUserWorldData(userId);
    if (!dbResult.ok) {
      const message = dbResult.errors.map((e) => `${e.table}: ${e.message}`).join("; ");
      console.error("resetToNewUser:", message);
      return { ok: false, error: message };
    }

    const starter = buildStarterHavenWorld(userId);
    const { hierarchy, surface_items } = splitWorldPayload(starter);
    setWorld(hierarchy);
    setPersistedSurfaceItems(surface_items);
    setRoomSurfaceItems(surfaceItemsByRoom(surface_items));
    setNeedsBrainDump(true);
    setWorldReady(true);
    worldLoadUserIdRef.current = userId;
    setWorldSaveError(null);
    setWorldLastSavedAt(null);
    setWorldAutosavePending(false);
    setPreviousSession(null);
    setCurrentRoomActivity(null);
    setCurrentLocation({
      districtId: null,
      islandId: null,
      buildingId: null,
      floorId: null,
      roomId: null,
    });
    setSurfaceState({
      whiteboardCards: [],
      deskDocuments: [],
      bookshelfDocs: [],
    });
    setMapOpen(false);
    setMapFocusId(null);
    setUserProfile(dbResult.profile);
    clearDeskPopupSeen(userId);
    requestUnityWorldSync(true);

    return { ok: true };
  }, [user?.id]);

  // ── Save world to Supabase (upsert) ──
  const saveWorld = async (worldData) => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = user?.id ?? session?.user?.id;
    if (!userId) return;
    const payload = buildWorldPayload(
      worldData,
      worldData.surface_items ?? persistedSurfaceItems
    );
    await persistWorldJson(payload);
  };

  const formatAuthError = (error) => {
    const msg = error?.message || "";
    if (/rate limit/i.test(msg) || error?.code === "over_email_send_rate_limit") {
      return "Something went wrong sending your link. Try again in a minute.";
    }
    return msg || "Something went wrong. Try again.";
  };

  const signUp = async ({ name, email, password, company }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          name,
          company: company || null,
        },
      },
    });
    if (error) return { error: formatAuthError(error) };

    const authUser = data.user;
    if (!authUser) return { error: "Sign up did not return a user. Check email confirmation settings." };

    if (data.session) {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (sessionError) return { error: formatAuthError(sessionError) };
      setUser(authUser);
    }

    // Trigger may have already created public.users — treat existing row as success
    let profile = data.session ? await fetchAppUser(authUser) : null;

    if (!profile && data.session) {
      const { error: profileError } = await supabase.from("users").upsert(
        {
          id: authUser.id,
          email,
          name,
          company: company || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "id" }
      );
      if (profileError) {
        profile = await fetchAppUser(authUser);
        if (!profile) return { error: formatAuthError(profileError) };
      } else {
        profile = await fetchAppUser(authUser);
      }
    }

    if (profile) {
      setAppUser(profile);
      if (data.session) setUser(authUser);
      return { error: null };
    }

    if (!data.session) {
      return {
        error: null,
        message: "Account created. If you are not signed in automatically, use Sign in with your email and password.",
      };
    }

    return { error: "Account created but profile could not be loaded. Try signing in." };
  };

  const signInWithPassword = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: formatAuthError(error) };
    if (data.user) {
      setUser(data.user);
      await loadAppUser(data.user);
    }
    return { error: null };
  };

  const signInWithMagicLink = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: getAuthRedirectUrl() },
    });
    return { error: error ? formatAuthError(error) : null };
  };

  const purgeSupabaseAuthStorage = () => {
    for (const storage of [localStorage, sessionStorage]) {
      for (const key of Object.keys(storage)) {
        if (key.startsWith("sb-") && key.includes("auth")) {
          storage.removeItem(key);
        }
      }
    }
  };

  // ── Sign out (flush world_json first while session is still valid) ──
  const signOut = useCallback(async () => {
    signingOutRef.current = true;
    try {
      if (worldDirtyRef.current || worldRef.current?.districts?.length) {
        await flushWorldJson();
      }
    } catch (e) {
      console.error("flushWorldJson on sign-out:", e);
    }
    clearAuthState();
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) console.error("Sign out error:", error);
    } catch (e) {
      console.error("Sign out error:", e);
    }
    purgeSupabaseAuthStorage();
    signingOutRef.current = false;
  }, [clearAuthState, flushWorldJson]);

  // ── Best-effort save when tab closes or is hidden ──
  useEffect(() => {
    const flushIfDirty = () => {
      if (!worldDirtyRef.current) return;
      void flushWorldJson();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushIfDirty();
    };
    window.addEventListener("beforeunload", flushIfDirty);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", flushIfDirty);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      cancelScheduledFlush();
    };
  }, [flushWorldJson]);

  // Open desk editors register a flush here so their last few keystrokes reach surfaceState
  // before a Lumen write is queued on top — otherwise the editor's delayed push lands after
  // the write and wipes it out.
  const deskFlushersRef = useRef(new Set());
  const registerDeskFlush = useCallback((flush) => {
    deskFlushersRef.current.add(flush);
    return () => deskFlushersRef.current.delete(flush);
  }, []);

  // A doc keeps one identity across saves: saving renames a new item's id (item-… → surface_<room>_N)
  // but the saved item keeps the original in sourceSurfaceItemId, and the live desk doc keeps the
  // original id. Every lookup below goes through these two so one doc is never treated as two.
  const liveDeskDocFor = (id, docs = surfaceStateRef.current?.deskDocuments || []) => {
    if (!id) return null;
    const saved = (persistedSurfaceItemsRef.current || []).find((i) => i.id === id);
    const ids = new Set([id, saved?.sourceSurfaceItemId].filter(Boolean));
    return docs.find((d) => ids.has(d.id) || ids.has(d.sourceSurfaceItemId)) || null;
  };
  const savedDeskItemFor = (id, items = persistedSurfaceItemsRef.current || []) =>
    id ? items.find((i) => i.id === id || (i.sourceSurfaceItemId && i.sourceSurfaceItemId === id)) || null : null;

  /** Desk docs in a room as they are right now: live desk edits first, then saved items. */
  const deskDocsInRoom = (roomId) => {
    const docs = [];
    const seen = new Set();
    for (const d of surfaceStateRef.current?.deskDocuments || []) {
      if ((d.roomId || d.room_id) !== roomId) continue;
      docs.push({ id: d.id, title: d.title, content: d.content || "", sourceSurfaceItemId: d.sourceSurfaceItemId });
      seen.add(d.id);
      if (d.sourceSurfaceItemId) seen.add(d.sourceSurfaceItemId);
    }
    for (const item of persistedSurfaceItemsRef.current || []) {
      if ((item.surface !== "desk" && item.surface !== "table") || item.room_id !== roomId) continue;
      if (seen.has(item.id) || (item.sourceSurfaceItemId && seen.has(item.sourceSurfaceItemId))) continue;
      docs.push({ id: item.id, title: item.title, content: item.content || "" });
    }
    return docs;
  };

  /** Reads a desk doc back from live state — used to confirm a write really landed. */
  const readDeskDocument = useCallback((docId) => {
    if (!docId) return null;
    const live = liveDeskDocFor(docId);
    if (live) return { id: live.id, title: live.title, content: live.content || "" };
    const saved = savedDeskItemFor(docId);
    return saved ? { id: saved.id, title: saved.title, content: saved.content || "" } : null;
  }, []);

  // ── Desk / table writes ──
  // update.append → add to the bottom of the target doc (doc_id, then title, then the room's only doc).
  // update.replace → swap the target doc's content (only after the user chose "Replace", or Undo).
  // update.forceNew or no id → create; a same-title doc is never silently overwritten
  //   (update.onTitleConflict "reject" returns a conflict for the user, default makes "Title (2)").
  // update.id → hydration and the desk's own new-doc flow: create the doc, or put a saved doc on
  //   the live desk. A doc already on the live desk is never replaced from here — the live copy
  //   is always at least as new as the saved one (saved items only catch up on the next save).
  // Returns { ok, status, docId, title, reason } — status is created | appended | updated |
  // unchanged | conflict | failed.
  const writeDeskItem = (update) => {
    const timestamp = Date.now();
    const roomId = update.room_id || currentLocationRef.current?.roomId || null;
    const byId = !!update.id && !update.append;

    deskFlushersRef.current.forEach((flush) => flush());

    let plan;
    let liveDoc = null;
    let savedItem = null;
    if (byId) {
      liveDoc = liveDeskDocFor(update.id);
      savedItem = (persistedSurfaceItemsRef.current || []).find((i) => i.id === update.id) || null;
      plan = liveDoc
        ? { kind: "unchanged", docId: liveDoc.id, title: liveDoc.title }
        : savedItem
          ? { kind: "show", docId: savedItem.id, title: savedItem.title }
          : { kind: "create", title: update.title || "Untitled" };
    } else {
      plan = planDeskWrite({
        docs: deskDocsInRoom(roomId),
        update,
        mode: update.replace ? "replace" : update.append ? "append" : "new",
        onTitleConflict: update.onTitleConflict === "reject" ? "reject" : "unique",
        forceAppend: update.forceAppend === true,
      });
    }

    // A saved doc from another room or a fresh load: put it on the live desk without touching content.
    const addLiveDeskDoc = (prev, content) => {
      if (liveDeskDocFor(update.id, prev.deskDocuments)) return prev;
      return {
        ...prev,
        deskDocuments: [...prev.deskDocuments, {
          id: update.id,
          title: savedItem?.title || update.title || "Untitled",
          content,
          version: 1,
          timestamp,
          sourceSurfaceItemId: update.sourceSurfaceItemId || update.id,
          roomId: savedItem?.room_id || roomId,
        }],
      };
    };

    if (plan.kind === "show") {
      setSurfaceState((prev) => addLiveDeskDoc(prev, savedItem?.content || ""));
      return { ok: true, status: "unchanged", docId: plan.docId, title: plan.title, reason: null };
    }

    if (plan.kind === "failed" || plan.kind === "conflict" || plan.kind === "unchanged") {
      return {
        ok: plan.kind === "unchanged",
        status: plan.kind,
        docId: plan.docId || null,
        title: plan.title || null,
        reason: plan.reason || null,
      };
    }

    // Append-style writes are merged against the content at apply time, so edits flushed just
    // above are kept underneath the new section.
    const nextContent = (currentContent, docTitle) => {
      if (plan.status === "replaced") return update.content || "";
      const merged = mergeIntoDeskDoc(currentContent, update.content, docTitle);
      // An in-place update was only safe against the doc as planned. If an edit flushed in just
      // before this write means Lumen's version would now drop a line, write nothing — the
      // read-back then reports it as not landed instead of losing that edit.
      if (plan.status === "updated" && merged.status !== "updated") return null;
      return merged.content;
    };
    // What the doc said before this write — lets a "Replace" be undone.
    const previousContent = plan.docId ? (deskDocsInRoom(roomId).find((d) => d.id === plan.docId)?.content ?? null) : null;
    const pushVersion = (doc) =>
      [...(doc.versions || []), { content: doc.content, savedAt: doc.timestamp }].slice(-10);

    let docId;
    let status;
    if (plan.kind === "create") {
      docId = update.id || `item-${timestamp}-${Math.random().toString(36).slice(2)}`;
      status = "created";
      const content = update.content || "";
      setPersistedSurfaceItems((prev) => {
        if (prev.some((i) => i.id === docId)) return prev;
        const next = [...prev, {
          id:                  docId,
          surface:             update.surface,
          title:               plan.title,
          content,
          room_id:             roomId,
          timestamp,
          sourceSurfaceItemId: update.sourceSurfaceItemId || docId,
          // Set when the write came from something the user asked for, not the first-entry
          // auto-populate — this is what "working on X" in the welcome-back line keys off.
          ...(update.user_edited ? { user_edited: true } : {}),
        }];
        setRoomSurfaceItems(surfaceItemsByRoom(next));
        return next;
      });
      setSurfaceState((prev) => {
        if (prev.deskDocuments.some((d) => d.id === docId)) return prev;
        return {
          ...prev,
          deskDocuments: [...prev.deskDocuments, {
            id: docId,
            title: plan.title,
            content,
            version: 1,
            timestamp,
            sourceSurfaceItemId: update.sourceSurfaceItemId || docId,
            roomId,
            ...(update.user_edited ? { user_edited: true } : {}),
          }],
        };
      });
    } else {
      docId = plan.docId;
      status = plan.status;
      setPersistedSurfaceItems((prev) => {
        const idx = prev.indexOf(savedDeskItemFor(docId, prev));
        if (idx === -1) return prev;
        const existing = prev[idx];
        const content = nextContent(existing.content, existing.title);
        if (content == null || content === existing.content) return prev;
        const next = [...prev];
        next[idx] = {
          ...existing,
          content,
          timestamp,
          versions: pushVersion(existing),
          ...(update.user_edited || existing.user_edited ? { user_edited: true } : {}),
        };
        setRoomSurfaceItems(surfaceItemsByRoom(next));
        return next;
      });
      setSurfaceState((prev) => {
        const idx = prev.deskDocuments.indexOf(liveDeskDocFor(docId, prev.deskDocuments));
        if (idx === -1) return prev;
        const existing = prev.deskDocuments[idx];
        const content = nextContent(existing.content, existing.title);
        if (content == null || content === existing.content) return prev;
        const nextDocs = [...prev.deskDocuments];
        nextDocs[idx] = {
          ...existing,
          content,
          version: (existing.version || 1) + 1,
          timestamp,
          versions: pushVersion(existing),
          ...(update.user_edited || existing.user_edited ? { user_edited: true } : {}),
        };
        return { ...prev, deskDocuments: nextDocs };
      });
    }

    worldDirtyRef.current = true;
    scheduleFlushWorldJson();
    return { ok: true, status, docId, title: plan.title, reason: null, previousContent };
  };

  // ── Add a surface item from Lumen surface_updates ──
  const addSurfaceItem = (update) => {
    if (update.surface === "desk" || update.surface === "table") return writeDeskItem(update);
    if (update.surface === "bulletin_board") {
      return addBulletinNote(
        { content: update.content, room_id: update.room_id || currentLocationRef.current?.roomId },
        "lumen"
      );
    }
    if (update.surface === "calendar") return applyLumenCalendarUpdate(update);
    if (update.surface === "my_day") return applyLumenMyDayUpdate(update);

    const id = `item-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = Date.now();

    if (update.surface === "bookshelf") {
      const docId = update.id || id;
      const incomingRoomId = update.room_id || currentLocationRef.current?.roomId || null;
      setPersistedSurfaceItems((prev) => {
        if (prev.some((i) => i.id === docId)) return prev;
        const next = [
          ...prev,
          {
            id: docId,
            surface: "bookshelf",
            title: update.title || "Untitled",
            content: update.content || "",
            room_id: incomingRoomId,
            timestamp,
            storagePath: update.storagePath || null,
            fileName: update.fileName || null,
            mimeType: update.mimeType || null,
            fileSize: update.fileSize || null,
          },
        ];
        setRoomSurfaceItems(surfaceItemsByRoom(next));
        worldDirtyRef.current = true;
        return next;
      });
    }

    setSurfaceState(prev => {
      switch (update.surface) {
        case "whiteboard":
          return { ...prev, whiteboardCards: [...prev.whiteboardCards, {
            id, type: update.type || "idea", surface: "whiteboard", timestamp,
            room_id: update.room_id || currentLocationRef.current?.roomId || null,
            content: update.type === "framework"
              ? (update.content || `${update.framework}: ${update.title || ""}`.trim())
              : update.type === "remove" ? `removed ${update.target}` : update.content,
            ...(update.type === "framework"
              ? { framework: update.framework, title: update.title, items: update.items, groups: update.groups }
              : {}),
            ...(update.replaces ? { replaces: String(update.replaces) } : {}),
            ...(update.type === "remove" ? { target: String(update.target || "") } : {}),
          }] };
        case "bookshelf":
          return { ...prev, bookshelfDocs: [...prev.bookshelfDocs, { id, title: update.title || update.content, content: update.content, savedAt: timestamp }] };
        default:
          return prev;
      }
    });

    if (update.surface === "bookshelf") {
      const docId = update.id || id;
      const known = (persistedSurfaceItemsRef.current || []).find((i) => i.id === docId);
      const isUnchangedResave = !!known && known.content === (update.content || "");
      if (!isUnchangedResave) {
        worldDirtyRef.current = true;
        scheduleFlushWorldJson();
      }
    }

    return { ok: true };
  };

  const sendToBookshelf = (docId) => {
    setSurfaceState(prev => {
      const doc = prev.deskDocuments.find(d => d.id === docId);
      if (!doc) return prev;
      return { ...prev, bookshelfDocs: [...prev.bookshelfDocs, { ...doc, savedAt: Date.now(), version: doc.version || 1 }] };
    });
  };

  const pullToDeskFromBookshelf = (docId) => {
    setSurfaceState(prev => {
      const doc = prev.bookshelfDocs.find(d => d.id === docId);
      if (!doc) return prev;
      if (prev.deskDocuments.some(d => d.id === docId)) return prev;
      return { ...prev, deskDocuments: [...prev.deskDocuments, { ...doc, timestamp: Date.now() }] };
    });
  };

  const setWhiteboardImage = (roomId, image) => {
    if (!roomId) return;
    setPersistedSurfaceItems((prev) => {
      const next = withWhiteboardImage(prev, roomId, image);
      setRoomSurfaceItems(surfaceItemsByRoom(next));
      return next;
    });
    worldDirtyRef.current = true;
    scheduleFlushWorldJson();
  };

  const setBulletinImage = (roomId, image) => {
    if (!roomId) return;
    setPersistedSurfaceItems((prev) => {
      const next = withBulletinImage(prev, roomId, image);
      setRoomSurfaceItems(surfaceItemsByRoom(next));
      return next;
    });
    worldDirtyRef.current = true;
    scheduleFlushWorldJson();
  };

  const patchSurfaceItem = (surfaceItemId, patch) => {
    setPersistedSurfaceItems((prev) => {
      const idx = prev.findIndex((item) => item.id === surfaceItemId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      setRoomSurfaceItems(surfaceItemsByRoom(next));
      worldDirtyRef.current = true;
      return next;
    });
  };

  const resetDeskAutosaveUi = useCallback(() => {
    setWorldAutosavePending(false);
  }, []);

  const updateDeskDocument = (docId, contentOrPatch) => {
    const rawPatch =
      typeof contentOrPatch === "string"
        ? { content: contentOrPatch, userEdit: true }
        : contentOrPatch || {};
    const userEdit = rawPatch.userEdit === true;
    const { userEdit: _userEdit, ...rest } = rawPatch;
    // Stamp documents the user actually edited. A desk doc is auto-created on first room entry,
    // so "a doc exists" says nothing about whether any work happened here — this flag is what
    // lets the welcome-back line tell "working on X" apart from "haven't started anything yet".
    const patch = userEdit ? { ...rest, user_edited: true } : rest;
    const roomId = currentLocationRef.current?.roomId || null;
    setSurfaceState((prev) => {
      const idx = prev.deskDocuments.findIndex((d) => d.id === docId);
      if (idx < 0) {
        return {
          ...prev,
          deskDocuments: [
            ...prev.deskDocuments,
            {
              id: docId,
              title: patch.title || "Untitled",
              content: patch.content || "",
              version: 1,
              roomId,
              sourceSurfaceItemId: docId,
              ...patch,
              timestamp: Date.now(),
            },
          ],
        };
      }
      return {
        ...prev,
        deskDocuments: prev.deskDocuments.map((d) =>
          d.id === docId ? { ...d, ...patch, timestamp: Date.now() } : d
        ),
      };
    });

    if (!userEdit) return;

    worldDirtyRef.current = true;
    setWorldSaveError(null);
    setWorldAutosavePending(true);
    scheduleFlushWorldJson();
  };

  const writePlannerItems = (update) => {
    const prev = persistedSurfaceItemsRef.current;
    const next = update(prev);
    if (next === prev) return;
    persistedSurfaceItemsRef.current = next;
    setPersistedSurfaceItems(next);
    setRoomSurfaceItems(surfaceItemsByRoom(next));
    plannerChangeVersionRef.current += 1;
    worldDirtyRef.current = true;
    setWorldSaveError(null);
    void flushWorldJson();
  };

  const addPlannerItem = (item) => {
    if (!item) return { ok: false, reason: "invalid" };
    writePlannerItems((prev) => [...prev, item]);
    return { ok: true, id: item.id };
  };

  const patchPlannerItem = (itemId, matches, applyPatch, patch) => {
    const resolve = (item) => applyPatch(item, typeof patch === "function" ? patch(item) : patch);
    const current = persistedSurfaceItemsRef.current.find((i) => i.id === itemId && matches(i));
    if (!current) return { ok: false, reason: "not_found" };
    if (!resolve(current)) return { ok: false, reason: "invalid" };
    writePlannerItems((prev) =>
      prev.map((i) => (i.id === itemId && matches(i) ? resolve(i) || i : i))
    );
    return { ok: true, id: itemId };
  };

  const removePlannerItem = (itemId, matches) => {
    if (!persistedSurfaceItemsRef.current.some((i) => i.id === itemId && matches(i))) {
      return { ok: false, reason: "not_found" };
    }
    writePlannerItems((prev) => prev.filter((i) => i.id !== itemId));
    return { ok: true, id: itemId };
  };

  const addCalendarEvent = (input) => addPlannerItem(createCalendarEvent(input));
  const updateCalendarEvent = (eventId, patch) =>
    patchPlannerItem(eventId, isCalendarEvent, applyCalendarEventPatch, patch);
  const deleteCalendarEvent = (eventId) => removePlannerItem(eventId, isCalendarEvent);
  const confirmCalendarEvent = (eventId) =>
    patchPlannerItem(eventId, isCalendarEvent, applyCalendarEventPatch, { status: "confirmed" });

  const applyLumenCalendarUpdate = (update) => {
    const plan = planLumenCalendarUpdate(update, persistedSurfaceItemsRef.current);
    if (!plan.ok) return { ok: false, reason: plan.reason, calendarReceipt: plan.receipt };
    let result = { ok: true };
    if (plan.op === "add") result = addPlannerItem(plan.event);
    if (plan.op === "change") result = updateCalendarEvent(plan.id, plan.patch);
    if (plan.op === "remove") result = deleteCalendarEvent(plan.id);
    if (plan.op === "confirm") result = confirmCalendarEvent(plan.id);
    if (result?.ok === false) {
      return { ok: false, reason: result.reason, calendarReceipt: { action: "failed", reason: result.reason, title: plan.receipt.title } };
    }
    return { ok: true, calendarReceipt: plan.receipt };
  };

  const addBulletinNote = (input, source = "user") =>
    addPlannerItem(createBulletinNote({ ...input, room_id: input.room_id || currentLocationRef.current?.roomId }, source));
  const updateBulletinNote = (noteId, patch) =>
    patchPlannerItem(noteId, isBulletinNote, applyBulletinNotePatch, patch);
  const deleteBulletinNote = (noteId) => removePlannerItem(noteId, isBulletinNote);

  const addDayBlock = (input) => addPlannerItem(createDayBlock(input));
  const updateDayBlock = (blockId, patch) => patchPlannerItem(blockId, isDayBlock, applyDayBlockPatch, patch);
  const deleteDayBlock = (blockId) => removePlannerItem(blockId, isDayBlock);
  const toggleDayBlock = (blockId) => updateDayBlock(blockId, (block) => ({ done: !block.done }));

  // Resizing and rolling over touch several blocks at once; one write keeps them together.
  const updateDayBlocks = (patches) => {
    const wanted = (patches || []).filter((p) => p?.id);
    if (!wanted.length) return { ok: true, count: 0 };
    const byId = new Map(wanted.map((p) => [p.id, p]));
    writePlannerItems((prev) =>
      prev.map((item) => {
        const patch = byId.get(item.id);
        if (!patch || !isDayBlock(item)) return item;
        const { id, ...fields } = patch;
        return applyDayBlockPatch(item, fields) || item;
      })
    );
    return { ok: true, count: wanted.length };
  };

  const setDayEnd = (dateKey, endTime) => {
    const marker = createDayEnd(dateKey, endTime);
    if (!marker) return { ok: false, reason: "invalid" };
    writePlannerItems((prev) => {
      const without = prev.filter((i) => !(isDayEnd(i) && i.date === dateKey));
      return [...without, marker];
    });
    return { ok: true, id: marker.id };
  };

  const runPlanChange = (result) => {
    if (!result.ok) return { ok: false, reason: result.reason, calendarReceipt: result.receipt };
    writePlannerItems(() => result.next);
    return { ok: true, calendarReceipt: result.receipt };
  };

  const acceptDayPlan = (planId) => runPlanChange(acceptPlan(persistedSurfaceItemsRef.current, planId));
  const dismissDayPlan = (planId) => runPlanChange(dismissPlan(persistedSurfaceItemsRef.current, planId));

  const applyLumenMyDayUpdate = (update) => {
    const now = new Date();
    const context = { todayKey: toDateKey(now), nowMinutes: now.getHours() * 60 + now.getMinutes() };
    const items = persistedSurfaceItemsRef.current;
    const type = String(update?.type || "plan").toLowerCase();
    if (type === "plan") return runPlanChange(buildPlan(update, items, context));
    if (type === "day_end") return runPlanChange(setDayEndFromLumen(update, items, context));
    if (type === "accept" || type === "dismiss") {
      const planId = update.plan_id || pendingPlanId(items, update.date || context.todayKey);
      const fallback = { ok: false, reason: "plan_gone", receipt: { kind: "plan", action: "failed", reason: "plan_gone" } };
      if (!planId) return runPlanChange(fallback);
      return runPlanChange(type === "accept" ? acceptPlan(items, planId) : dismissPlan(items, planId));
    }
    return runPlanChange({ ok: false, reason: "unknown_type", receipt: { kind: "plan", action: "failed", reason: "unknown_type" } });
  };

  const toggleBulletinItem = (itemId) => updateBulletinNote(itemId, (note) => ({ done: !note.done }));

  const deleteSurfaceItem = (surface, itemId) => {
    if (surface === "bulletin_board") return deleteBulletinNote(itemId);
    if (surface === "calendar") return deleteCalendarEvent(itemId);
    setSurfaceState(prev => {
      switch (surface) {
        case "whiteboard":    return { ...prev, whiteboardCards: prev.whiteboardCards.filter(c => c.id !== itemId) };
        case "desk":          return { ...prev, deskDocuments: prev.deskDocuments.filter(d => d.id !== itemId) };
        case "bookshelf":     return { ...prev, bookshelfDocs: prev.bookshelfDocs.filter(d => d.id !== itemId) };
        default:              return prev;
      }
    });
  };

  // Permanently delete a desk document from both the live UI state and Supabase.
  //
  // Fix 1: issue a real, immediate Supabase write instead of just scheduling an
  // autosave. Any autosave that was already in-flight is awaited first so our
  // authoritative delete write is always the last one to land.
  const deleteDeskDocument = useCallback(async (docId) => {
    // Capture the correct post-delete state synchronously from refs — before any
    // React batching or in-flight flush can mutate them.
    const filteredDeskDocs = (surfaceStateRef.current?.deskDocuments || []).filter(
      d => d.id !== docId && d.sourceSurfaceItemId !== docId
    );
    const filteredPersistedItems = persistedSurfaceItemsRef.current.filter(
      i => i.id !== docId && i.sourceSurfaceItemId !== docId
    );

    // Update React state immediately so the UI reflects the deletion.
    setSurfaceState(prev => ({
      ...prev,
      deskDocuments: prev.deskDocuments.filter(
        d => d.id !== docId && d.sourceSurfaceItemId !== docId
      ),
    }));
    setPersistedSurfaceItems(filteredPersistedItems);
    setRoomSurfaceItems(surfaceItemsByRoom(filteredPersistedItems));

    // Fix 2 hook: increment version so any concurrent persistWorldJson call
    // (from an in-flight autosave) won't re-sync its stale server response.
    deleteVersionRef.current += 1;

    // Cancel any pending debounced flush, then wait for any in-flight flush to
    // settle. The in-flight flush may write the deleted doc to Supabase — that's
    // OK because our write below will immediately overwrite it with the correct
    // filtered state.
    cancelScheduledFlush();
    if (flushInFlightRef.current) {
      try { await flushInFlightRef.current; } catch (_) {}
    }

    const hierarchy = worldRef.current;
    if (!hierarchy?.districts?.length) return;

    setWorldSaveError(null);
    setWorldAutosavePending(true);
    // Clear dirty flag to suppress scheduleFlushWorldJson guard during our write.
    worldDirtyRef.current = false;

    // Build the payload directly from the captured (correct) filtered state so
    // we're immune to any React state lag from the setSurfaceState calls above.
    const mergedItems = surfaceItemsWithDeskEdits(filteredPersistedItems, filteredDeskDocs);
    const payload = buildWorldPayload(hierarchy, mergedItems);

    // Register our write in flushInFlightRef so the close button (and any other
    // concurrent caller of flushWorldJson) can chain on it rather than racing.
    const run = (async () => {
      const result = await persistWorldJson(payload);
      if (!result?.ok) worldDirtyRef.current = true;
      return result;
    })();
    flushInFlightRef.current = run;
    try {
      await run;
    } finally {
      if (flushInFlightRef.current === run) flushInFlightRef.current = null;
    }
  }, []);

  const moveWhiteboardCard = (cardId, x, y) => {
    setSurfaceState(prev => ({
      ...prev,
      whiteboardCards: prev.whiteboardCards.map(c => c.id === cardId ? { ...c, x, y } : c)
    }));
  };

  // ── World hierarchy helpers ──
  const loadParsedWorld = async (parsed) => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = user?.id ?? session?.user?.id;
    if (!userId) {
      const msg = "Not signed in — sign in before Go Spatial.";
      console.error("loadParsedWorld:", msg);
      return { ok: false, error: msg };
    }
    const merged = defensiveMergeBuildings(parsed, world);
    const safeParsed = { ...merged, districts: carryBuildingModels(merged?.districts, world?.districts) };
    const parsedItems = Array.isArray(safeParsed.surface_items) ? safeParsed.surface_items : [];
    const parsedIds = new Set(parsedItems.map((i) => i?.id).filter(Boolean));
    const keptItems = persistedSurfaceItemsRef.current.filter(
      (i) => (isPlannerItem(i) || i?.id) && !parsedIds.has(i?.id)
    );
    const normalizedWorld = normalizeWorldForSupabase(userId, {
      ...safeParsed,
      surface_items: [...parsedItems, ...keptItems],
    });
    const previousDistrictIds = new Set((world?.districts || []).map((d) => d.id));
    const addsDistrict = !previousDistrictIds.size ||
      (normalizedWorld.districts || []).some((d) => !previousDistrictIds.has(d.id));
    const normalized = addsDistrict
      ? { ...normalizedWorld, districts: compactWorldLayout(normalizedWorld.districts) }
      : normalizedWorld;
    const { hierarchy, surface_items } = splitWorldPayload(normalized);
    setWorld(hierarchy);
    setPersistedSurfaceItems(surface_items);
    setRoomSurfaceItems(surfaceItemsByRoom(surface_items));
    setNeedsBrainDump(isStarterHavenOnlyWorld(normalized.districts));
    setWorldReady(true);
    setJustBuiltWorld(true);
    supabase.from('user_profile').update({ last_activity: null }).eq('user_id', userId);
    setPreviousSession((prev) => (prev ? { ...prev, last_activity: null } : prev));
    return persistWorldJson(normalized);
  };

  const findSurfaceItemById = (hotspotRef) =>
    resolveSurfaceItemFromHotspot(hotspotRef, persistedSurfaceItems);

  /** Load table hotspot content into desk UI without mutating persisted surface_items. */
  const hydrateDeskFromSurfaceItem = (item) => {
    if (!item) return;
    addSurfaceItem({
      id: item.id,
      surface: item.surface === "table" ? "table" : "desk",
      title: item.title || item.content?.substring?.(0, 40) || "Table",
      content: item.content || "",
      room_id: item.room_id,
      sourceSurfaceItemId: item.id,
    });
  };

  const enterRoomForSurfaceItem = (item) => {
    if (!item?.room_id) return;
    const location = locationFromRoomId(world.districts, item.room_id);
    if (location.roomId) enterRoom(location);
  };

  /** Replace desk with this room's table items only — clears other rooms' docs from the UI. */
  const loadDeskDocumentsForRoom = (roomId) => {
    const deskDocuments = deskDocumentsForRoom(
      roomId,
      persistedSurfaceItemsRef.current,
      surfaceStateRef.current?.deskDocuments || []
    );
    setSurfaceState((prev) => ({ ...prev, deskDocuments }));
  };

  const enterRoom = (location) => {
    const prevRoomId = currentLocationRef.current?.roomId ?? null;
    const nextRoomId = location?.roomId || null;
  
    // ── On-leave: summarize the room being exited (feature branch) ──
    if (user?.id && prevRoomId && prevRoomId !== nextRoomId) {
      (async () => {
        try {
          const result = await summarizeRoom(prevRoomId, persistedSurfaceItemsRef.current);
          if (result) {
            const { error } = await supabase
              .from("room_activity")
              .update({ last_summary: result.summary, next_step: result.next_step })
              .eq("user_id", user.id)
              .eq("room_id", prevRoomId);
            if (error) console.warn("enterRoom: failed to save room summary", error);
            else setRoomSummaryVersion((v) => v + 1);
          }
        } catch (e) {
          console.warn("enterRoom: summary generation failed", e);
        }
      })();
    }
  
    setCurrentLocation(location);
    currentLocationRef.current = location;
    setMapOpen(false);
    loadDeskDocumentsForRoom(nextRoomId);
  
    if (worldDirtyRef.current && prevRoomId !== nextRoomId) {
      void flushWorldJson();
    }
  
    // ── On-enter: persist location + load room activity (feature branch) ──
    if (user?.id && nextRoomId) {
      let room_name = null, building_name = null, island_name = null;
      outer: for (const district of world.districts) {
        for (const island of district.islands || []) {
          for (const building of island.buildings || []) {
            for (const floor of building.floors || []) {
              const room = (floor.rooms || []).find((r) => r.id === nextRoomId);
              if (room) {
                room_name = room.name || null;
                building_name = building.name || null;
                island_name = island.name || null;
                break outer;
              }
            }
          }
        }
      }
      supabase
        .from("user_profile")
        .update({
          last_room_id: nextRoomId,
          last_activity: {
            room_id: nextRoomId,
            room_name,
            building_name,
            island_name,
            saved_at: new Date().toISOString(),
          },
        })
        .eq("user_id", user.id)
        .then(({ error }) => {
          if (error) console.warn("enterRoom: failed to persist last location", error);
        });
      setPreviousSession((prev) => ({
        ...(prev || {}),
        last_room_id: nextRoomId,
        last_activity: {
          room_id: nextRoomId,
          room_name,
          building_name,
          island_name,
          saved_at: new Date().toISOString(),
        },
      }));
      supabase
        .from("room_activity")
        .select("*")
        .eq("user_id", user.id)
        .eq("room_id", nextRoomId)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) console.warn("enterRoom: failed to read room_activity", error);
          setCurrentRoomActivity(data || null);
          const now = new Date().toISOString();
          supabase
            .from("room_activity")
            .upsert(
              {
                user_id: user.id,
                room_id: nextRoomId,
                room_name,
                building_name,
                island_name,
                last_activity: {
                  room_id: nextRoomId,
                  room_name,
                  building_name,
                  island_name,
                  entered_at: now,
                },
                updated_at: now,
              },
              { onConflict: "user_id,room_id" }
            )
            .then(({ error: upsertErr }) => {
              if (upsertErr) console.warn("enterRoom: failed to upsert room_activity", upsertErr);
            });
        });
    } else if (user?.id && !nextRoomId && prevRoomId) {
      supabase
        .from("user_profile")
        .update({ last_activity: null })
        .eq("user_id", user.id)
        .then(({ error }) => {
          if (error) console.warn("enterRoom: failed to clear last_activity", error);
        });
      setPreviousSession((prev) => (prev ? { ...prev, last_activity: null } : prev));
    }
  };
  

  /** Persist room unlock in world_json and push live to Unity (SetRoomUnlockState). */
  const setRoomUnlockState = (roomId, unlocked) => {
    if (!roomId) return;
    setWorld((prev) => {
      const districts = JSON.parse(JSON.stringify(prev.districts || []));
      forEachRoom(districts, (room) => {
        if (room.id === roomId) room.is_unlocked = unlocked;
      });
      const updated = { ...prev, districts };
      saveWorld(updated);
      return updated;
    });
    getUnityBridgeCommands()?.setRoomUnlockState(roomId, unlocked);
  };

  const updateMapPosition = useCallback((type, id, x, y) => {
    if (!id) return;
    setWorld(prev => ({
      ...prev,
      districts: (prev.districts || []).map(d => ({
        ...d,
        islands: (d.islands || []).map(isl => {
          if (type === "island" && isl.id === id) {
            return { ...isl, map_x: x, map_y: y };
          }
          return {
            ...isl,
            buildings: (isl.buildings || []).map(b =>
              type === "building" && b.id === id ? { ...b, map_x: x, map_y: y } : b
            ),
          };
        }),
      })),
    }));
    worldDirtyRef.current = true;
    scheduleFlushWorldJson();
  }, [scheduleFlushWorldJson]);

  const getCurrentRoom = () => {
    if (!currentLocation.roomId) return null;
    for (const district of world.districts) {
      for (const island of district.islands || []) {
        for (const building of island.buildings || []) {
          for (const floor of building.floors || []) {
            const room = (floor.rooms || []).find(r => r.id === currentLocation.roomId);
            if (room) return { room, floor, building, island, district };
          }
        }
      }
    }
    return null;
  };

  const addDistrict = (district) => setWorld(prev => {
    const updated = { ...prev, districts: [...prev.districts, district] };
    saveWorld(updated);
    return updated;
  });

  const addIsland = (districtId, island) => setWorld(prev => {
    const updated = { ...prev, districts: prev.districts.map(d => d.id !== districtId ? d : { ...d, islands: [...(d.islands || []), island] }) };
    saveWorld(updated);
    return updated;
  });

  const addBuilding = (districtId, islandId, building) => setWorld(prev => {
    const updated = { ...prev, districts: prev.districts.map(d => d.id !== districtId ? d : {
      ...d, islands: (d.islands || []).map(i => i.id !== islandId ? i : { ...i, buildings: [...(i.buildings || []), building] })
    })};
    saveWorld(updated);
    return updated;
  });

  const addFloor = (districtId, islandId, buildingId, floor) => setWorld(prev => {
    const updated = { ...prev, districts: prev.districts.map(d => d.id !== districtId ? d : {
      ...d, islands: (d.islands || []).map(i => i.id !== islandId ? i : {
        ...i, buildings: (i.buildings || []).map(b => b.id !== buildingId ? b : { ...b, floors: [...(b.floors || []), floor] })
      })
    })};
    saveWorld(updated);
    return updated;
  });

  const addRoom = (districtId, islandId, buildingId, floorId, room) => setWorld(prev => {
    const updated = { ...prev, districts: prev.districts.map(d => d.id !== districtId ? d : {
      ...d, islands: (d.islands || []).map(i => i.id !== islandId ? i : {
        ...i, buildings: (i.buildings || []).map(b => b.id !== buildingId ? b : {
          ...b, floors: (b.floors || []).map(f => f.id !== floorId ? f : { ...f, rooms: [...(f.rooms || []), room] })
        })
      })
    })};
    saveWorld(updated);
    return updated;
  });

  const renameNode = (id, newName) => {
    const rename = (nodes) => nodes.map(n => {
      if (n.id === id) return { ...n, name: newName };
      return { ...n, islands: n.islands ? rename(n.islands) : undefined, buildings: n.buildings ? rename(n.buildings) : undefined, floors: n.floors ? rename(n.floors) : undefined, rooms: n.rooms ? rename(n.rooms) : undefined };
    });
    setWorld(prev => {
      const updated = { ...prev, districts: rename(prev.districts) };
      saveWorld(updated);
      return updated;
    });
  };

  const deleteNode = (id) => {
    const remove = (nodes) => nodes.filter(n => n.id !== id).map(n => ({
      ...n,
      islands:   n.islands   ? remove(n.islands)   : undefined,
      buildings: n.buildings ? remove(n.buildings) : undefined,
      floors:    n.floors    ? remove(n.floors)    : undefined,
      rooms:     n.rooms     ? remove(n.rooms)     : undefined,
    }));
    setWorld(prev => {
      const updated = { ...prev, districts: applyIslandUnlockRules(remove(prev.districts)) };
      saveWorld(updated);
      return updated;
    });
  };

  const getBreadcrumb = () => {
    const ctx = getCurrentRoom();
    if (!ctx) return [];
    return [
      { label: world.name,          level: "world"    },
      { label: ctx.district.name,   level: "district" },
      { label: ctx.island.name,     level: "island"   },
      { label: ctx.building.name,   level: "building" },
      { label: ctx.floor.name,      level: "floor"    },
      { label: ctx.room.name,       level: "room"     },
    ];
  };

  // ── Log user activity ──
  const logEvent = useCallback(async (eventType, payload = {}) => {
    if (!user) return;
    try {
      await supabase.from("user_activity_log").insert({
        user_id:       user.id,
        event_type:    eventType,
        event_payload: payload,
        hour_of_day:   new Date().getHours(),
        day_of_week:   new Date().getDay(),
      });
    } catch (e) {
      console.error("logEvent error:", e);
    }
  }, [user]);
  const isUserTnCNotAccepted = useCallback(() => {
    return userProfile && user !== null &&  user.id !== null && userProfile.user_id !== null && userProfile.legal_accepted_at === null;
  },[user, userProfile]);

  const plannerSurfaceState = useMemo(() => ({
    ...surfaceState,
    calendarEvents: persistedSurfaceItems.filter(isCalendarEvent),
    plannerItems: persistedSurfaceItems.filter((i) => isCalendarEvent(i) || isDayBlock(i) || isDayEnd(i)),
    bulletinItems: notesForRoom(persistedSurfaceItems, currentLocation.roomId),
  }), [surfaceState, persistedSurfaceItems, currentLocation.roomId]);

  return (
    <SpatialContext.Provider value={{
      user, appUser, authReady, sessionBootstrapping, authCallbackError, signInWithMagicLink, signOut,
      world, setWorld, worldReady, needsBrainDump,
      worldLoadFailed,
      worldSaving, worldSaveError, worldLastSavedAt, worldAutosavePending,
      saveWorld, flushWorldJson, getUnityWorldPayload, resetToNewUser, setWhiteboardImage, setBulletinImage,
      roomSurfaceItems, persistedSurfaceItems, persistedSurfaceItemsRef,
      findSurfaceItemById, hydrateDeskFromSurfaceItem, enterRoomForSurfaceItem,
      currentLocation, setCurrentLocation, currentLocationRef,
      mapOpen, setMapOpen,
      mapFocusId, setMapFocusId,
      loadParsedWorld, enterRoom, setRoomUnlockState, updateMapPosition,
      getCurrentRoom, getBreadcrumb,
      addDistrict, addIsland, addBuilding, addFloor, addRoom,
      renameNode, deleteNode,
      surfaceState: plannerSurfaceState,
      addSurfaceItem,
      sendToBookshelf,
      pullToDeskFromBookshelf,
      updateDeskDocument, resetDeskAutosaveUi,
      registerDeskFlush, readDeskDocument,
      toggleBulletinItem,
      deleteSurfaceItem,
      addCalendarEvent, updateCalendarEvent, deleteCalendarEvent, confirmCalendarEvent,
      acceptDayPlan, dismissDayPlan,
      addBulletinNote, updateBulletinNote, deleteBulletinNote,
      addDayBlock, updateDayBlock, deleteDayBlock, toggleDayBlock, updateDayBlocks, setDayEnd,
      deleteDeskDocument,
      moveWhiteboardCard,
      userProfile, setUserProfile, profileLoading, profileChecked,
      previousSession,
      currentRoomActivity,
      roomSummaryVersion,
      justBuiltWorld, setJustBuiltWorld,
      logEvent,isUserTnCNotAccepted
    }}>
      {children}
    </SpatialContext.Provider>
  );
}

export function useSpatial() {
  return useContext(SpatialContext);
}
